import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import projectCommunicationService from '../../../services/projectCommunicationService'
import { getAllProjects } from '../../../services/projectService'
import userService from '../../../services/userService'

const COMM_TYPES = ['Internal', 'Client', 'Status Update', 'Issue', 'Decision', 'General']
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed']

const ProjectCommunicationForm = ({ mode = 'create' }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const isEditMode = mode === 'edit' || path.includes('/edit/')
  const isViewMode = mode === 'view' || path.includes('/view/')
  const isReadOnly = isViewMode
  const recordId = id

  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [users, setUsers] = useState([])
  const [replies, setReplies] = useState([])
  const [replyText, setReplyText] = useState('')
  const [replyLoading, setReplyLoading] = useState(false)
  const [formData, setFormData] = useState({
    communicationNumber: '',
    subject: '',
    message: '',
    project: '',
    communicationType: 'Internal',
    priority: 'Medium',
    status: 'Open',
    recipients: []
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadMasterData()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadRecord()
    }
  }, [recordId, isEditMode, isViewMode])

  const loadMasterData = async () => {
    try {
      const [projRes, usersRes] = await Promise.all([
        getAllProjects({ limit: 1000 }),
        userService.getAllUsers({ limit: 500 })
      ])
      setProjects(projRes?.projects || projRes?.data || [])
      const userList = usersRes?.data || usersRes?.users || (Array.isArray(usersRes) ? usersRes : [])
      setUsers(userList)
    } catch (e) {
      console.error(e)
    }
  }

  const loadRecord = async () => {
    try {
      setDataLoading(true)
      const res = await projectCommunicationService.getById(recordId)
      if (res.success && res.data) {
        const d = res.data
        setFormData({
          communicationNumber: d.communicationNumber || '',
          subject: d.subject || '',
          message: d.message || '',
          project: d.project?._id || d.project || '',
          communicationType: d.communicationType || 'Internal',
          priority: d.priority || 'Medium',
          status: d.status || 'Open',
          recipients: (d.recipients || []).map((r) => r._id || r)
        })
        setReplies(d.replies || [])
      } else {
        toast.error('Communication not found')
        navigate('/projects/communications')
      }
    } catch {
      toast.error('Failed to load communication')
      navigate('/projects/communications')
    } finally {
      setDataLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const handleRecipientsChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (o) => o.value)
    setFormData((prev) => ({ ...prev, recipients: selected }))
  }

  const validate = () => {
    const next = {}
    if (!formData.project) next.project = 'Project is required'
    if (!formData.subject?.trim()) next.subject = 'Subject is required'
    if (!formData.message?.trim()) next.message = 'Message is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) {
      toast.error('Please fix validation errors')
      return
    }
    const payload = {
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      project: formData.project,
      communicationType: formData.communicationType,
      priority: formData.priority,
      status: formData.status,
      recipients: formData.recipients
    }
    try {
      setLoading(true)
      const res = isEditMode
        ? await projectCommunicationService.update(recordId, payload)
        : await projectCommunicationService.create(payload)
      if (res.success) {
        toast.success(isEditMode ? 'Updated' : 'Created')
        navigate('/projects/communications')
      } else {
        toast.error(res.message || 'Save failed')
      }
    } catch {
      toast.error('Failed to save')
    } finally {
      setLoading(false)
    }
  }

  const handleAddReply = async () => {
    if (!replyText.trim()) {
      toast.error('Enter a reply message')
      return
    }
    try {
      setReplyLoading(true)
      const res = await projectCommunicationService.addReply(recordId, replyText.trim())
      if (res.success) {
        toast.success('Reply added')
        setReplyText('')
        setReplies(res.data?.replies || [])
        if (res.data?.status) {
          setFormData((prev) => ({ ...prev, status: res.data.status }))
        }
      } else {
        toast.error(res.message || 'Failed to add reply')
      }
    } catch {
      toast.error('Failed to add reply')
    } finally {
      setReplyLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Communication', path: '/projects/communications', onClick: () => navigate('/projects/communications') },
    { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
  ]

  if (dataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Communication' : isViewMode ? 'View Communication' : 'New Communication'}</h1>
          <p>Project messages, status updates, and discussion threads</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header"><h2>Communication</h2></div>
        <form className="form-content" onSubmit={handleSubmit}>
          <div className="section-header">
            <h3>Communication Details</h3>
          </div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Communication #</label>
              <input
                readOnly
                className="form-input"
                value={formData.communicationNumber || 'Auto-generated on save'}
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label required">Project</label>
              <select name="project" value={formData.project} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                <option value="">Select project</option>
                {projects.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>
                    {p.projectNumber} — {p.projectName}
                  </option>
                ))}
              </select>
              {errors.project && <p className="form-error">{errors.project}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select name="communicationType" value={formData.communicationType} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {COMM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Message</h3>
            </div>
          </div>

          <div className="grid-row-2">
            <div className="form-group">
              <label className="form-label required">Subject</label>
              <input type="text" name="subject" value={formData.subject} onChange={handleChange} className="form-input" disabled={isReadOnly} maxLength={300} />
              {errors.subject && <p className="form-error">{errors.subject}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-input" disabled={isReadOnly}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label required">Message</label>
            <textarea name="message" value={formData.message} onChange={handleChange} className="form-input" rows={6} disabled={isReadOnly} />
            {errors.message && <p className="form-error">{errors.message}</p>}
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Recipients</h3>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Recipients (users)</label>
            <select
              multiple
              value={formData.recipients}
              onChange={handleRecipientsChange}
              className="form-input"
              disabled={isReadOnly}
              style={{ minHeight: '100px' }}
            >
              {users.map((u) => (
                <option key={u._id || u.id} value={u._id || u.id}>
                  {u.name || u.userEmail || u.email}
                </option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>
              Hold Ctrl/Cmd to select multiple recipients
            </p>
          </div>

          {(isViewMode || isEditMode) && (
            <div className="form-section-divider">
              <div className="section-header"><h3>Replies ({replies.length})</h3></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                {replies.length === 0 && (
                  <p style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No replies yet.</p>
                )}
                {replies.map((r, idx) => (
                  <div
                    key={r._id || idx}
                    style={{
                      border: '1px solid #E5E7EB',
                      padding: '12px',
                      background: '#FAFBFC'
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#222448', marginBottom: '6px', fontWeight: 600 }}>
                      {r.author?.name || r.author?.userEmail || 'User'}
                      {' · '}
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Add reply</label>
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} className="form-input" rows={3} />
                <button type="button" className="btn-primary" style={{ marginTop: '8px' }} onClick={handleAddReply} disabled={replyLoading}>
                  {replyLoading ? 'Posting...' : 'Post Reply'}
                </button>
              </div>
            </div>
          )}

          {!isViewMode && (
            <div className="form-actions">
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => navigate('/projects/communications')}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '12px' }}>
              <button type="button" className="btn-secondary" onClick={() => navigate('/projects/communications')}>Back</button>
              <button type="button" className="btn-primary" onClick={() => navigate(`/projects/communications/edit/${recordId}`)}>Edit</button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ProjectCommunicationForm
